import { Injectable, computed, inject, Injector } from '@angular/core';
import { DataService } from './data.service';
import { AuthService } from '../auth/auth.service';
import { ActingContextService } from './acting-context.service';
import { collection, doc, writeBatch, query, where, getDocs, updateDoc, deleteDoc, getDoc, addDoc, WriteBatch, setDoc } from 'firebase/firestore'; // FIX: Consolidated imports
import { FIRESTORE, FUNCTIONS, getFunctionName } from '../firebase.config';
import { NewCommitmentPayload, ClientCommitment, LoanStatus, ClientLead, AccountType, FullLoanDeal, HeadsOfTermsDbData, LoanLedgerEntry, BorrowerLoanApplication, BorrowerLoanApplicationPayload } from '../models/loan.model';
import { UserProfile } from '../models/user.model';
import { TicketService } from './ticket.service';
import { NotificationService } from './notification.service';
import { LOAN_STATUS, LOAN_LEDGER_ENTRY_TYPE, TICKET_STATUS } from '../constants';
import { httpsCallable } from 'firebase/functions';
import { AppCacheService } from './app-cache.service';
import type { ClientRelationship, AccessibleClient } from '../models/relationship.model';
import { getFinancialActionIdempotencyKey } from '../utils/financial-action-idempotency';
import { ClientRelationshipService } from './client-relationship.service';

export interface ClientDetails extends UserProfile {
  commitments: (ClientCommitment & { dealName: string; dealId: string; dealStatus: LoanStatus })[];
  totalCommitted: number;
  activeLoanDealsCount: number;
  fundedLoanDealsCount: number;
  finishedLoanDealsCount: number;
  hasOpenTickets: boolean;
}

export interface BorrowerLinkedDeal {
  id: string;
  name: string;
  status: LoanStatus;
  grossLoanAmount: number;
  loanStartDate: string;
  loanEndDate: string;
}

export interface BorrowerDetails extends UserProfile {
  linkedDeals: BorrowerLinkedDeal[];
  applications: BorrowerLoanApplication[];
  latestApplication: BorrowerLoanApplication | null;
  activeLoanDealsCount: number;
  fundedLoanDealsCount: number;
  finishedLoanDealsCount: number;
}

export interface DeleteClientAccountResult {
  success: boolean;
  uid: string;
  deleted: {
    commitments: number;
    loanLedgerEntries: number;
    tickets: number;
    notifications: number;
    redemptionStatements: number;
    relationships: number;
    approvalRequests: number;
    activityLogs: number;
    corporateKycTokens: number;
    kycSubmissions: number;
    corporateKycSubmissions: number;
    clientLeads: number;
    storageFiles: number;
    authDeleted: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private dataService: DataService = inject(DataService);
  // FIX: Explicitly type injected service to resolve 'unknown' type error.
  private authService: AuthService = inject(AuthService);
  private injector = inject(Injector);
  private ticketService: TicketService = inject(TicketService);
  private notificationService = inject(NotificationService);
  private cache = inject(AppCacheService);
  private relationshipService = inject(ClientRelationshipService);
  private adminNotificationService: any; // Lazy loaded to avoid circular dependency
  private readonly executeFinancialActionCallable = httpsCallable<
    { action: string; idempotencyKey: string; payload: Record<string, unknown> },
    { result?: unknown }
  >(FUNCTIONS, getFunctionName('executeFinancialAction'));
  private readonly deleteClientAccountCallable = httpsCallable<{ uid: string }, DeleteClientAccountResult>(
    FUNCTIONS,
    getFunctionName('deleteClientAccount')
  );

  constructor() { }

  private async executeFinancialAction<TResult = unknown>(
    action: string,
    payload: Record<string, unknown>,
    options?: { idempotencyKey?: string }
  ): Promise<TResult> {
    const response = await this.executeFinancialActionCallable({
      action,
      idempotencyKey: getFinancialActionIdempotencyKey({
        action,
        payload,
        idempotencyKey: options?.idempotencyKey,
      }),
      payload,
    });

    return ((response.data as { result?: TResult } | undefined)?.result ?? null) as TResult;
  }

  private shouldUseServerFinancialActions(): boolean {
    return true;
  }

  private anonymizeName(userId: string): string {
    // Basic hash for frontend-backend parity with the scrub script
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    const hex = Math.abs(hash).toString(16).substring(0, 8).toUpperCase();
    return `CLIENT-${hex}`;
  }

  private async getCachedUserProfile(userId: string): Promise<UserProfile | null> {
    const fromDataService = this.dataService.getUserById(userId);
    if (fromDataService) {
      return fromDataService;
    }

    return await this.cache.readThrough(`user-profile:${userId}`, async () => {
      const userDoc = await getDoc(doc(FIRESTORE, 'users', userId));
      return userDoc.exists() ? { uid: userId, ...userDoc.data() } as UserProfile : null;
    }, 5 * 60 * 1000);
  }

  private normalizeBorrowerLookupValue(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  private dealMatchesBorrowerProfile(deal: FullLoanDeal, borrower: UserProfile): boolean {
    const dealBorrower = deal.borrower;
    if (!dealBorrower) {
      return false;
    }

    if (dealBorrower.borrowerUserId && dealBorrower.borrowerUserId === borrower.uid) {
      return true;
    }

    const borrowerRegistration = this.normalizeBorrowerLookupValue(borrower.companyRegistrationNumber);
    const dealRegistration = this.normalizeBorrowerLookupValue(dealBorrower.registrationCode);
    if (borrowerRegistration && dealRegistration && borrowerRegistration === dealRegistration) {
      return true;
    }

    const borrowerNameCandidates = [
      borrower.displayName,
      borrower.companyName,
      borrower.directorName,
    ]
      .map(candidate => this.normalizeBorrowerLookupValue(candidate))
      .filter(candidate => !!candidate);

    const dealBorrowerName = this.normalizeBorrowerLookupValue(dealBorrower.borrowerName);
    return !!dealBorrowerName && borrowerNameCandidates.includes(dealBorrowerName);
  }

  // Raw Data Signals
  allClients = computed(() => {
    const clients = this.dataService.users().filter(u => u.role?.toLowerCase() === 'client');
    console.log('ClientService: allClients count:', clients.length, 'total users:', this.dataService.users().length);
    if (clients.length > 0) {
      console.log('ClientService: first client sample:', clients[0].displayName, 'role:', clients[0].role);
    }
    return clients;
  });
  allCommitments = this.dataService.clientCommitments;
  allNotifications = this.dataService.notifications;
  allClientLeads = this.dataService.clientLeads;
  allBorrowers = computed(() => this.dataService.users().filter(u => u.role?.toLowerCase() === 'borrower'));

  // Lookup map for resolving anonymized client names
  userLookupMap = computed(() => {
    const users = this.dataService.users();
    return new Map<string, string>(users.map(u => [u.uid, u.displayName || u.email || 'Unknown User']));
  });

  // Computed Signals for UI


  clientsWithDetails = computed<ClientDetails[]>(() => {
    const clients = this.allClients();
    const commitments = this.allCommitments();
    const deals = this.dataService.allDeals();
    const tickets = this.ticketService.allTicketsSorted();

    const dealsMap = new Map<string, FullLoanDeal>(deals.map(d => [d.loanDeal.id, d]));

    return clients.map(client => {
      const clientCommitments = commitments
        .filter(c => c.userId === client.uid)
        .map(c => {
          const deal = dealsMap.get(c.loanDealId);
          return {
            ...c,
            dealName: deal?.loanDeal.loanDealName ?? 'Unknown Deal',
            dealId: deal?.loanDeal.id ?? '',
            dealStatus: deal?.loanDeal.status ?? LOAN_STATUS.DRAFT,
          };
        });

      const totalCommitted = clientCommitments.reduce((sum, c) => sum + c.committedAmount, 0);

      const dealIds = new Set<string>(clientCommitments.map(c => c.loanDealId));
      let activeLoanDealsCount = 0;
      let fundedLoanDealsCount = 0;
      let finishedLoanDealsCount = 0;

      for (const id of dealIds) {
        const deal = dealsMap.get(id);
        if (deal) {
          if (deal.loanDeal.status === LOAN_STATUS.ACTIVE) activeLoanDealsCount++;
          if (deal.loanDeal.status === LOAN_STATUS.FUNDED || deal.loanDeal.status === LOAN_STATUS.EXTENDED) fundedLoanDealsCount++;
          if (deal.loanDeal.status === LOAN_STATUS.FINISHED) finishedLoanDealsCount++;
        }
      }

      const hasOpenTickets = tickets.some(t => t.userId === client.uid && t.status === TICKET_STATUS.OPEN);

      return {
        ...client,
        commitments: clientCommitments,
        totalCommitted,
        activeLoanDealsCount,
        fundedLoanDealsCount,
        finishedLoanDealsCount,
        hasOpenTickets,
      };
    });
  });

  borrowersWithDetails = computed<BorrowerDetails[]>(() => {
    const borrowers = this.allBorrowers();
    const deals = this.dataService.allDeals();
    const applicationsByBorrower = this.dataService.borrowerApplicationsByBorrowerUserId();

    return borrowers.map(borrower => {
      const linkedDeals = deals
        .filter(deal => this.dealMatchesBorrowerProfile(deal, borrower))
        .map(deal => ({
          id: deal.loanDeal.id,
          name: deal.loanDeal.loanDealName,
          status: deal.loanDeal.status,
          grossLoanAmount: deal.loanDeal.grossLoanAmount,
          loanStartDate: deal.loanDeal.loanStartDate,
          loanEndDate: deal.loanDeal.loanEndDate,
        }));
      const applications = [...(applicationsByBorrower.get(borrower.uid) ?? [])]
        .sort((left, right) => new Date(right.submittedAt).getTime() - new Date(left.submittedAt).getTime());

      let activeLoanDealsCount = 0;
      let fundedLoanDealsCount = 0;
      let finishedLoanDealsCount = 0;

      linkedDeals.forEach(linkedDeal => {
        if (linkedDeal.status === LOAN_STATUS.ACTIVE) {
          activeLoanDealsCount++;
        }
        if (linkedDeal.status === LOAN_STATUS.FUNDED || linkedDeal.status === LOAN_STATUS.EXTENDED) {
          fundedLoanDealsCount++;
        }
        if (linkedDeal.status === LOAN_STATUS.FINISHED) {
          finishedLoanDealsCount++;
        }
      });

      return {
        ...borrower,
        linkedDeals,
        applications,
        latestApplication: applications[0] ?? null,
        activeLoanDealsCount,
        fundedLoanDealsCount,
        finishedLoanDealsCount,
      };
    });
  });

  borrowerDealsForCurrentUser = computed<FullLoanDeal[]>(() => {
    const borrower = this.authService.userProfile();
    if (!borrower || borrower.role !== 'Borrower') {
      return [];
    }

    return this.dataService
      .allDeals()
      .filter(deal => this.dealMatchesBorrowerProfile(deal, borrower));
  });

  currentBorrowerDetails = computed<BorrowerDetails | null>(() => {
    const borrower = this.authService.userProfile();
    if (!borrower || borrower.role !== 'Borrower') {
      return null;
    }

    const linkedDeals = this.borrowerDealsForCurrentUser().map(deal => ({
      id: deal.loanDeal.id,
      name: deal.loanDeal.loanDealName,
      status: deal.loanDeal.status,
      grossLoanAmount: deal.loanDeal.grossLoanAmount,
      loanStartDate: deal.loanDeal.loanStartDate,
      loanEndDate: deal.loanDeal.loanEndDate,
    }));

    let activeLoanDealsCount = 0;
    let fundedLoanDealsCount = 0;
    let finishedLoanDealsCount = 0;

    linkedDeals.forEach(linkedDeal => {
      if (linkedDeal.status === LOAN_STATUS.ACTIVE) {
        activeLoanDealsCount++;
      }
      if (linkedDeal.status === LOAN_STATUS.FUNDED || linkedDeal.status === LOAN_STATUS.EXTENDED) {
        fundedLoanDealsCount++;
      }
      if (linkedDeal.status === LOAN_STATUS.FINISHED) {
        finishedLoanDealsCount++;
      }
    });

    return {
      ...borrower,
      linkedDeals,
      applications: [],
      latestApplication: null,
      activeLoanDealsCount,
      fundedLoanDealsCount,
      finishedLoanDealsCount,
    };
  });

  pendingCommitmentsValue = computed(() => {
    return this.allCommitments()
      .filter(c => c.isConfirmed && !c.fundsReceived)
      .reduce((sum, c) => sum + c.committedAmount, 0);
  });

  recentCommitments = computed(() => {
    return [...this.allCommitments()]
      .sort((a, b) => new Date(b.commitmentDate).getTime() - new Date(a.commitmentDate).getTime())
      .slice(0, 5);
  });

  // Client-facing data - respects acting context for trustees/read-only users
  commitmentsForCurrentUser = computed(() => {
    // Use Injector to get ActingContextService lazily to avoid circular dependency
    // and ensure we're tracking the signal
    const actingContext = this.injector.get(ActingContextService);
    const effectiveUserId = actingContext.effectiveUserId() || this.authService.userProfile()?.uid;

    if (!effectiveUserId) return [];
    return this.allCommitments().filter(c => c.userId === effectiveUserId);
  });

  // Filtered notifications for the current acting user
  notificationsForCurrentUser = computed(() => {
    const actingContext = this.injector.get(ActingContextService);
    const effectiveUserId = actingContext.effectiveUserId() || this.authService.userProfile()?.uid;

    if (!effectiveUserId) return [];
    return this.allNotifications().filter(n => n.userId === effectiveUserId);
  });

  // Computed Signals for UI - Updated to use filtered notifications
  unreadNotifications = computed(() => this.notificationsForCurrentUser().filter(n => !n.read));

  

  

  

  // Methods
  async addCommitments(
    dealId: string,
    dealName: string,
    newCommitments: (NewCommitmentPayload & { trancheId?: string })[],
    isAdmin = false,
    options?: { activateOnValueDate?: boolean }
  ): Promise<string[]> {
    const sanitizedCommitments = newCommitments.map(commitment => this.sanitizeNewCommitmentPayload(commitment));

    if (this.shouldUseServerFinancialActions()) {
      const result = await this.executeFinancialAction<{ createdIds?: string[] }>('ADD_COMMITMENTS', {
        dealId,
        commitments: sanitizedCommitments,
        options,
      });
      return result?.createdIds ?? [];
    }

    console.log('[COMMIT DEBUG] addCommitments called with:', {
      dealId,
      dealName,
      commitmentCount: sanitizedCommitments.length,
      commitments: sanitizedCommitments,
      isAdmin
    });
    const batch = writeBatch(FIRESTORE);
    const createdIds: string[] = [];
    const now = new Date().toISOString();
    const commitmentsCollection = collection(FIRESTORE, 'clientCommitments');

    for (const commitment of sanitizedCommitments) {
      console.log('[COMMIT DEBUG] Processing commitment for userId:', commitment.userId);

      try {
        const userDoc = await getDoc(doc(FIRESTORE, 'users', commitment.userId));
        console.log('[COMMIT DEBUG] User doc exists:', userDoc.exists());

        if (!userDoc.exists()) {
          console.error(`[COMMIT DEBUG] User profile not found for userId: ${commitment.userId}`);
          continue;
        }

        const userProfile = userDoc.data() as UserProfile;
        console.log('[COMMIT DEBUG] User profile loaded:', {
          uid: userProfile.uid,
          displayName: userProfile.displayName,
          accountCount: userProfile.accounts?.length || 0
        });

        const clientAccount = userProfile.accounts?.find(acc => acc.id === commitment.clientAccountId);

        if (!clientAccount) {
          console.error(`[COMMIT DEBUG] Client account not found for accountId: ${commitment.clientAccountId}`, {
            availableAccounts: userProfile.accounts?.map(a => a.id)
          });
          continue;
        }

        console.log('[COMMIT DEBUG] Client account found:', {
          accountId: clientAccount.id,
          accountType: clientAccount.accountType,
          bankName: clientAccount.bankName
        });

        const commitmentRef = doc(commitmentsCollection);
        createdIds.push(commitmentRef.id);
        const effectiveValueDate = commitment.valueDate ? this.normalizeCommitmentValueDate(commitment.valueDate) : null;
        const shouldActivateOnValueDate = Boolean(options?.activateOnValueDate && effectiveValueDate);
        const operatorId = this.authService.userProfile()?.uid || 'system';
        const anonName = this.anonymizeName(commitment.userId);

        const newCommitmentData: ClientCommitment = {
          id: commitmentRef.id,
          loanDealId: dealId,
          userId: commitment.userId,
          clientName: anonName,
          clientDisplayName: userProfile.displayName,
          clientAccountId: clientAccount.id,
          accountType: clientAccount.accountType,
          committedAmount: commitment.committedAmount,
          currentPrincipal: commitment.committedAmount,
          totalPrincipalRedeemed: 0,
          commitmentDate: now,
          isConfirmed: shouldActivateOnValueDate,
          fundsReceived: shouldActivateOnValueDate,
          ...(shouldActivateOnValueDate ? {
            dateConfirmed: now,
            confirmedByUserId: operatorId,
            dateFundsReceived: effectiveValueDate as string,
          } : {}),
          ...(commitment.trancheId && { trancheId: commitment.trancheId }),
        };

        console.log('[COMMIT DEBUG] Created commitment data:', {
          id: newCommitmentData.id,
          userId: newCommitmentData.userId,
          clientName: newCommitmentData.clientName,
          amount: newCommitmentData.committedAmount
        });

        console.log('[COMMIT DEBUG] Writing to paths:', {
          topLevel: `clientCommitments/${commitmentRef.id}`,
          userSubcollection: `users/${commitment.userId}/clientCommitments/${commitmentRef.id}`
        });

        batch.set(commitmentRef, newCommitmentData);
        const userCommitmentRef = doc(FIRESTORE, `users/${commitment.userId}/clientCommitments/${commitmentRef.id}`);
        batch.set(userCommitmentRef, newCommitmentData);

        if (shouldActivateOnValueDate && effectiveValueDate) {
          const ledgerRef = doc(collection(FIRESTORE, 'loanLedger'));
          const ledgerEntry: Omit<LoanLedgerEntry, 'id'> = {
            loanDealId: dealId,
            date: now,
            valueDate: effectiveValueDate,
            description: `Client Funds Received - ${anonName} (${clientAccount.accountType})`,
            amount: commitment.committedAmount,
            userId: commitment.userId,
            commitmentId: commitmentRef.id,
            entryType: LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN,
            _anonymized: true,
            accountType: clientAccount.accountType,
            createdBy: operatorId,
          };
          batch.set(ledgerRef, ledgerEntry);
        }
      } catch (error) {
        console.error('[COMMIT DEBUG] Error processing commitment:', error);
        throw error;
      }
    }

    if (!isAdmin && sanitizedCommitments.length > 0) {
      const totalAmount = sanitizedCommitments.reduce((sum, c) => sum + c.committedAmount, 0);
      console.log('[COMMIT DEBUG] Creating notification for userId:', newCommitments[0].userId);
      await this.notificationService.notifyCommitmentReceived(
        sanitizedCommitments[0].userId,
        dealId,
        dealName,
        totalAmount,
        batch
      );
    }

    console.log('[COMMIT DEBUG] Committing batch...');
    try {
      await batch.commit();
      console.log('[COMMIT DEBUG] Batch committed successfully!');
    } catch (error) {
      console.error('[COMMIT DEBUG] Error committing batch:', error);
      throw error;
    }
    return createdIds;
  }

  private sanitizeNewCommitmentPayload(
    commitment: NewCommitmentPayload & { trancheId?: string }
  ): NewCommitmentPayload & { trancheId?: string } {
    const sanitized: NewCommitmentPayload & { trancheId?: string } = {
      userId: commitment.userId,
      clientName: commitment.clientName,
      clientAccountId: commitment.clientAccountId,
      committedAmount: commitment.committedAmount,
    };

    if (commitment.valueDate) {
      sanitized.valueDate = commitment.valueDate;
    }

    if (commitment.trancheId) {
      sanitized.trancheId = commitment.trancheId;
    }

    return sanitized;
  }

  private normalizeCommitmentValueDate(valueDate: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(valueDate)) {
      const [year, month, day] = valueDate.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0)).toISOString();
    }

    const parsed = new Date(valueDate);
    if (Number.isNaN(parsed.getTime())) {
      throw new Error(`Invalid commitment value date: ${valueDate}`);
    }

    return parsed.toISOString();
  }

  async updateCommitmentFlags(
    commitmentId: string,
    userId: string,
    updates: {
      isConfirmed?: boolean;
      fundsReceived?: boolean;
      headsOfTermsSigned?: boolean;
      headsOfTermsPdfUrl?: string;
    },
    confirmedByUserId?: string,
    fundsReceivedDate?: string // Optional: custom date for funds received
  ): Promise<void> {
    const serverUpdatePayload: Record<string, unknown> = { ...updates };
    const localUpdatePayload: Record<string, unknown> = { ...updates };

    if (updates.isConfirmed) {
      localUpdatePayload.dateConfirmed = new Date().toISOString();
      if (confirmedByUserId) {
        localUpdatePayload.confirmedByUserId = confirmedByUserId;
      }
    }
    if (updates.fundsReceived) {
      // Use the provided date if available, otherwise default to now
      localUpdatePayload.dateFundsReceived = fundsReceivedDate || new Date().toISOString();
    }
    if (updates.headsOfTermsSigned) {
      localUpdatePayload.headsOfTermsSignedDate = new Date().toISOString();
    }
    // Also ensure if funds are received, it is also confirmed
    if (updates.fundsReceived === true) {
      serverUpdatePayload.isConfirmed = true;
      localUpdatePayload.isConfirmed = true;
      if (!localUpdatePayload.dateConfirmed) {
        localUpdatePayload.dateConfirmed = new Date().toISOString();
      }
    }

    // If un-confirming, also un-receive funds
    if (updates.isConfirmed === false) {
      serverUpdatePayload.fundsReceived = false;
      localUpdatePayload.fundsReceived = false;
    }

    await this.executeFinancialAction('UPDATE_COMMITMENT_FLAGS', {
      commitmentId,
      userId,
      updates: serverUpdatePayload,
      confirmedByUserId,
      fundsReceivedDate,
    });

    if (updates.fundsReceived === false) {
      localUpdatePayload.dateFundsReceived = null;
    }
    if (updates.headsOfTermsSigned === false) {
      localUpdatePayload.headsOfTermsSignedDate = null;
      localUpdatePayload.headsOfTermsPdfUrl = null;
    }

    this.dataService.updateLocalCommitment(commitmentId, localUpdatePayload as Partial<ClientCommitment>);
  }

  async manuallyConfirmFundsReceived(commitmentId: string, userId: string): Promise<void> {
    await this.updateCommitmentFlags(commitmentId, userId, { fundsReceived: true });
  }

  async removeCommitment(commitmentId: string, userId: string): Promise<void> {
    const userRole = this.authService.currentUserRole();
    if (this.shouldUseServerFinancialActions()) {
      await this.executeFinancialAction('DELETE_COMMITMENT', {
        commitmentId,
        userId,
      });
      return;
    }

    const batch = writeBatch(FIRESTORE);
    const topLevelRef = doc(FIRESTORE, 'clientCommitments', commitmentId);
    const nestedRef = doc(FIRESTORE, `users/${userId}/clientCommitments/${commitmentId}`);

    batch.delete(nestedRef);

    if (userRole === 'Admin') {
      batch.delete(topLevelRef);

      const ledgerQuery = query(
        collection(FIRESTORE, 'loanLedger'),
        where('sourceId', '==', commitmentId),
        where('entryType', '==', LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN)
      );
      const existingLedgerSnapshot = await getDocs(ledgerQuery);
      if (!existingLedgerSnapshot.empty) {
        existingLedgerSnapshot.forEach(doc => batch.delete(doc.ref));
      }
    } else {
      this.dataService.removeLocalCommitment(commitmentId);
    }

    await batch.commit();
  }

  async updateCommitmentAmount(commitmentId: string, newAmount: number, userId?: string): Promise<void> {
    console.log('[UPDATE DEBUG] updateCommitmentAmount called:', { commitmentId, newAmount, userId });

    try {
      let targetUserId = userId;

      // If userId is not provided, we must fetch it from the top-level document.
      // Note: This read might fail if the user (Client/Trustee) doesn't have read access to top-level.
      if (!targetUserId) {
        const commitmentDoc = await getDoc(doc(FIRESTORE, 'clientCommitments', commitmentId));
        console.log('[UPDATE DEBUG] Commitment doc exists:', commitmentDoc.exists());

        if (!commitmentDoc.exists()) {
          throw new Error(`Commitment with id ${commitmentId} not found.`);
        }
        targetUserId = (commitmentDoc.data() as ClientCommitment).userId;
      }

      console.log('[UPDATE DEBUG] Commitment userId:', targetUserId);
      if (this.shouldUseServerFinancialActions()) {
        await this.executeFinancialAction('UPDATE_COMMITMENT_AMOUNT', {
          commitmentId,
          newAmount,
          userId: targetUserId,
        });
      } else {
        const batch = writeBatch(FIRESTORE);
        const nestedRef = doc(FIRESTORE, `users/${targetUserId}/clientCommitments/${commitmentId}`);
        const updatePayload = { committedAmount: newAmount };

        console.log('[UPDATE DEBUG] Updating paths:', {
          topLevel: `clientCommitments/${commitmentId}`,
          nested: `users/${targetUserId}/clientCommitments/${commitmentId}`
        });

        batch.update(doc(FIRESTORE, 'clientCommitments', commitmentId), updatePayload);
        batch.update(nestedRef, updatePayload);
        await batch.commit();
      }
      this.dataService.updateLocalCommitment(commitmentId, { committedAmount: newAmount });
    } catch (error) {
      console.error('[UPDATE DEBUG] Error updating commitment:', error);
      throw error;
    }
  }

  async reallocateCommitment(
    originalCommitmentId: string,
    newAllocations: { clientAccountId: string; amount: number }[],
    confirmedByUserId: string
  ): Promise<void> {
    const topLevelRef = doc(FIRESTORE, 'clientCommitments', originalCommitmentId);
    const commitmentDoc = await getDoc(topLevelRef);

    if (!commitmentDoc.exists()) {
      throw new Error(`Commitment with id ${originalCommitmentId} not found.`);
    }

    const originalCommitment = commitmentDoc.data() as ClientCommitment;
    const userId = originalCommitment.userId;
    const userDoc = await getDoc(doc(FIRESTORE, 'users', userId));
    const userProfile = userDoc.data() as UserProfile;

    if (!userProfile || !userProfile.accounts) {
      throw new Error('User profile or accounts not found.');
    }

    await this.executeFinancialAction('REALLOCATE_COMMITMENT', {
      originalCommitmentId,
      newAllocations: newAllocations.map(allocation => {
        const account = userProfile.accounts.find(a => a.id === allocation.clientAccountId);

        if (!account) {
          throw new Error(`Account ${allocation.clientAccountId} not found`);
        }

        return {
          clientAccountId: allocation.clientAccountId,
          amount: allocation.amount,
          accountType: account.accountType,
        };
      }),
      confirmedByUserId,
    });

    if (!this.shouldUseServerFinancialActions()) {
      const dealRef = doc(FIRESTORE, 'loanDeals', originalCommitment.loanDealId);
      const dealDoc = await getDoc(dealRef);
      const dealName = dealDoc.exists() ? (dealDoc.data() as any).loanDealName : 'Unknown Deal';

      await this.notificationService.notifyCommitmentConfirmed(originalCommitment, dealName);
    }
  }

  async saveHeadsOfTermsData(commitmentId: string, data: HeadsOfTermsDbData): Promise<void> {
    await this.executeFinancialAction('SAVE_HEADS_OF_TERMS_DATA', {
      commitmentId,
      data,
    });

    this.dataService.updateLocalCommitment(commitmentId, {
      headsOfTermsData: data,
    });
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    const actingContext = this.injector.get(ActingContextService);
    const effectiveUserId = actingContext.effectiveUserId() || this.authService.userProfile()?.uid;
    if (!effectiveUserId) return;

    const notification = this.notificationsForCurrentUser().find(n => n.id === notificationId);
    const userId = notification?.userId || effectiveUserId;
    const nestedRef = doc(FIRESTORE, `users/${userId}/notifications/${notificationId}`);

    await updateDoc(nestedRef, { read: true });

    try {
      await updateDoc(doc(FIRESTORE, 'notifications', notificationId), { read: true });
    } catch (error) {
      console.warn('Unable to update top-level notification read state:', error);
    }
  }

  async markAllNotificationsAsRead(): Promise<void> {
    const actingContext = this.injector.get(ActingContextService);
    const effectiveUserId = actingContext.effectiveUserId() || this.authService.userProfile()?.uid;
    if (!effectiveUserId) return;

    const unread = this.unreadNotifications().filter(n => n.userId === effectiveUserId);
    if (unread.length === 0) return;

    const batch = writeBatch(FIRESTORE);
    const isAdmin = this.authService.currentUserRole() === 'Admin';
    unread.forEach(notification => {
      const nestedRef = doc(FIRESTORE, `users/${effectiveUserId}/notifications/${notification.id}`);
      batch.update(nestedRef, { read: true });
      if (isAdmin) {
        const topLevelRef = doc(FIRESTORE, 'notifications', notification.id);
        batch.update(topLevelRef, { read: true });
      }
    });
    await batch.commit();

    if (!isAdmin) {
      await Promise.allSettled(
        unread.map(notification =>
          updateDoc(doc(FIRESTORE, 'notifications', notification.id), { read: true })
        )
      );
    }
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    await this.dataService.updateDocument(`users/${userId}`, updates);
    this.cache.invalidate(`user-profile:${userId}`);

    const currentUser = this.authService.userProfile();
    if (currentUser && currentUser.uid === userId) {
      this.authService.userProfile.set({
        ...currentUser,
        ...updates
      });
    }
  }

  async createBorrowerProfile(input: {
    displayName: string;
    email?: string;
    title?: string;
    givenName?: string;
    surname?: string;
    companyName?: string;
    companyRegistrationNumber?: string;
    jurisdiction?: string;
    directorName?: string;
    directorEmail?: string;
    accountantEmail?: string;
  }): Promise<string> {
    const admin = this.authService.userProfile();
    if (!admin || admin.role !== 'Admin') {
      throw new Error('Only admins can create borrower profiles');
    }

    const now = new Date().toISOString();
    const randomSuffix = Math.random().toString(36).slice(2, 10);
    const borrowerId = `borrower_${Date.now()}_${randomSuffix}`;
    const displayName = input.displayName.trim();
    const companyName = (input.companyName || displayName).trim();
    const fallbackEmail = `${borrowerId}@borrower.local`;
    const directorEmail = (input.directorEmail || '').trim();
    const accountantEmail = (input.accountantEmail || '').trim();
    const email = (input.email || directorEmail || '').trim() || fallbackEmail;

    const payload: UserProfile = {
      uid: borrowerId,
      email,
      displayName,
      title: (input.title || '').trim() || undefined,
      givenName: (input.givenName || '').trim() || undefined,
      surname: (input.surname || '').trim() || undefined,
      role: 'Borrower',
      entityType: 'Legal Entity',
      companyName,
      companyRegistrationNumber: (input.companyRegistrationNumber || '').trim(),
      jurisdiction: (input.jurisdiction || '').trim(),
      directorName: (input.directorName || '').trim(),
      directorEmail: directorEmail || undefined,
      accountantEmail: accountantEmail || undefined,
      accountantEmailAddedAt: accountantEmail ? now : undefined,
      invitedBy: admin.uid,
      lastInvitedAt: now,
    };

    await setDoc(doc(FIRESTORE, 'users', borrowerId), payload);
    this.cache.invalidate(`user-profile:${borrowerId}`);
    return borrowerId;
  }

  async submitBorrowerLoanApplication(payload: BorrowerLoanApplicationPayload): Promise<string> {
    const borrower = this.authService.userProfile();
    if (!borrower || borrower.role !== 'Borrower') {
      throw new Error('Only borrower accounts can submit loan applications');
    }

    const now = new Date().toISOString();
    const applicationRef = doc(collection(FIRESTORE, 'borrowerApplications'));
    const normalizedApplication: BorrowerLoanApplication = {
      id: applicationRef.id,
      borrowerUserId: borrower.uid,
      borrowerName: (payload.borrowerName || borrower.displayName || '').trim(),
      companyName: (payload.companyName || borrower.companyName || borrower.displayName || '').trim(),
      companyRegistrationNumber: (payload.companyRegistrationNumber || borrower.companyRegistrationNumber || '').trim() || undefined,
      jurisdiction: (payload.jurisdiction || borrower.jurisdiction || '').trim() || undefined,
      directorName: (payload.directorName || borrower.directorName || '').trim() || undefined,
      directorEmail: (payload.directorEmail || borrower.directorEmail || '').trim() || undefined,
      accountantEmail: (payload.accountantEmail || borrower.accountantEmail || '').trim() || undefined,
      status: 'SUBMITTED',
      submittedAt: now,
      updatedAt: now,
      loanDeal: payload.loanDeal,
      securityDetails: payload.securityDetails,
      properties: payload.properties,
      notes: payload.notes?.trim() || undefined,
    };

    const batch = writeBatch(FIRESTORE);
    batch.set(applicationRef, normalizedApplication);
    batch.set(doc(FIRESTORE, `users/${borrower.uid}/borrowerApplications/${applicationRef.id}`), normalizedApplication);
    batch.set(doc(FIRESTORE, 'users', borrower.uid), {
      latestBorrowerApplicationId: applicationRef.id,
      latestBorrowerApplicationSubmittedAt: now,
      latestBorrowerApplicationStatus: 'SUBMITTED',
    }, { merge: true });

    await batch.commit();
    return applicationRef.id;
  }

  async updateBorrowerProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    const existing = await this.getCachedUserProfile(userId);
    if (!existing || existing.role !== 'Borrower') {
      throw new Error('Borrower profile not found');
    }

    await this.updateUserProfile(userId, updates);
  }

  async deleteBorrowerProfile(userId: string): Promise<void> {
    const admin = this.authService.userProfile();
    if (!admin || admin.role !== 'Admin') {
      throw new Error('Only admins can delete borrower profiles');
    }

    const borrower = await this.getCachedUserProfile(userId);
    if (!borrower || borrower.role !== 'Borrower') {
      throw new Error('Borrower profile not found');
    }

    const hasLinkedDeals = this.dataService.allDeals().some(deal => this.dealMatchesBorrowerProfile(deal, borrower));
    if (hasLinkedDeals) {
      throw new Error('Cannot delete borrower while linked to one or more loan deals.');
    }

    const [borrowerApplicationsSnapshot, nestedBorrowerApplicationsSnapshot] = await Promise.all([
      getDocs(query(collection(FIRESTORE, 'borrowerApplications'), where('borrowerUserId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/borrowerApplications`)),
    ]);

    const applicationDeleteBatch = writeBatch(FIRESTORE);
    applicationDeleteBatch.delete(doc(FIRESTORE, 'users', userId));
    borrowerApplicationsSnapshot.docs.forEach(docSnap => applicationDeleteBatch.delete(docSnap.ref));
    nestedBorrowerApplicationsSnapshot.docs.forEach(docSnap => applicationDeleteBatch.delete(docSnap.ref));
    await applicationDeleteBatch.commit();
    this.cache.invalidate(`user-profile:${userId}`);
  }

  async deleteClientAccount(userId: string): Promise<DeleteClientAccountResult> {
    const result = await this.deleteClientAccountCallable({ uid: userId });
    return result.data;
  }

  async updateAccountantEmail(userId: string, email: string): Promise<void> {
    const updates: Partial<UserProfile> = {
      accountantEmail: email,
      accountantEmailAddedAt: new Date().toISOString()
    };
    await this.updateUserProfile(userId, updates);

    // Auto-refresh: Update the local user profile state
    const currentUser = this.authService.userProfile();
    if (currentUser && currentUser.uid === userId) {
      this.authService.userProfile.set({
        ...currentUser,
        ...updates
      });
    }
  }

  async removeAccountantEmail(userId: string): Promise<void> {
    const updates: Partial<UserProfile> = {
      accountantEmail: null,
      accountantEmailAddedAt: null
    };
    await this.updateUserProfile(userId, updates);

    // Auto-refresh: Update the local user profile state
    const currentUser = this.authService.userProfile();
    if (currentUser && currentUser.uid === userId) {
      this.authService.userProfile.set({
        ...currentUser,
        ...updates
      });
    }
  }

  private shouldFallbackToFirestoreDeletion(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const candidate = error as {
      code?: string;
      message?: string;
      customData?: { httpStatus?: number };
    };

    const code = candidate.code?.toLowerCase() ?? '';
    const message = candidate.message?.toLowerCase() ?? '';
    const httpStatus = candidate.customData?.httpStatus;

    return code.includes('internal')
      || code.includes('unavailable')
      || message.includes('cors')
      || message.includes('failed to fetch')
      || message.includes('network request failed')
      || httpStatus === 404;
  }

  private async deleteClientAccountViaFirestore(userId: string): Promise<DeleteClientAccountResult> {
    const userRef = doc(FIRESTORE, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('Client profile not found.');
    }

    const clientProfile = userSnap.data() as UserProfile;
    if (clientProfile.role !== 'Client') {
      throw new Error('Only client accounts can be deleted.');
    }

    const clientEmail = typeof clientProfile.email === 'string' ? clientProfile.email.trim() : '';

    const [
      commitmentSnapshot,
      nestedCommitmentSnapshot,
      ticketSnapshot,
      nestedTicketSnapshot,
      notificationSnapshot,
      nestedNotificationSnapshot,
      statementSnapshot,
      relationshipAsPrimarySnapshot,
      relationshipAsAssociatedSnapshot,
      approvalAsClientSnapshot,
      approvalAsUserSnapshot,
      approvalAsTrusteeSnapshot,
      activityLogSnapshot,
      tokenSnapshot,
      individualKycSnapshot,
      corporateKycSnapshot,
      kycDraftSnapshot,
      corporateKycDraftSnapshot,
      assignedUserSnapshot,
      leadSnapshot,
    ] = await Promise.all([
      getDocs(query(collection(FIRESTORE, 'clientCommitments'), where('userId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/clientCommitments`)),
      getDocs(query(collection(FIRESTORE, 'tickets'), where('userId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/tickets`)),
      getDocs(query(collection(FIRESTORE, 'notifications'), where('userId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/notifications`)),
      getDocs(query(collection(FIRESTORE, 'redemptionStatements'), where('userId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'clientRelationships'), where('primaryClientId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'clientRelationships'), where('associatedUserId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'approvalRequests'), where('clientId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'approvalRequests'), where('userId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'approvalRequests'), where('trusteeId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'clientActivityLog'), where('clientId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'corporateKycTokens'), where('entityUserId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/kycFactFinds`)),
      getDocs(collection(FIRESTORE, `users/${userId}/corporateKycFactFinds`)),
      getDocs(collection(FIRESTORE, `users/${userId}/kycDrafts`)),
      getDocs(collection(FIRESTORE, `users/${userId}/corporateKycDrafts`)),
      getDocs(query(collection(FIRESTORE, 'users'), where('assignedClientIds', 'array-contains', userId))),
      clientEmail
        ? getDocs(query(collection(FIRESTORE, 'clientLeads'), where('contactEmail', '==', clientEmail)))
        : Promise.resolve({ docs: [], size: 0 } as Awaited<ReturnType<typeof getDocs>>),
    ]);

    const commitmentIds = commitmentSnapshot.docs.map(docSnap => docSnap.id);
    const loanLedgerDocs = await this.getLoanLedgerDocsForCommitments(commitmentIds);

    const deleted: DeleteClientAccountResult['deleted'] = {
      commitments: commitmentSnapshot.size,
      loanLedgerEntries: loanLedgerDocs.length,
      tickets: ticketSnapshot.size,
      notifications: notificationSnapshot.size,
      redemptionStatements: statementSnapshot.size,
      relationships: new Set([
        ...relationshipAsPrimarySnapshot.docs.map(docSnap => docSnap.id),
        ...relationshipAsAssociatedSnapshot.docs.map(docSnap => docSnap.id),
      ]).size,
      approvalRequests: new Set([
        ...approvalAsClientSnapshot.docs.map(docSnap => docSnap.id),
        ...approvalAsUserSnapshot.docs.map(docSnap => docSnap.id),
        ...approvalAsTrusteeSnapshot.docs.map(docSnap => docSnap.id),
      ]).size,
      activityLogs: activityLogSnapshot.size,
      corporateKycTokens: tokenSnapshot.size,
      kycSubmissions: individualKycSnapshot.size,
      corporateKycSubmissions: corporateKycSnapshot.size,
      clientLeads: leadSnapshot.size,
      storageFiles: 0,
      authDeleted: false,
    };

    for (const assignedUserDoc of assignedUserSnapshot.docs) {
      const assignedClientIds = Array.isArray(assignedUserDoc.data().assignedClientIds)
        ? assignedUserDoc.data().assignedClientIds
        : [];
      const nextAssignedIds = assignedClientIds.filter((clientId: string) => clientId !== userId);

      if (nextAssignedIds.length !== assignedClientIds.length) {
        await updateDoc(assignedUserDoc.ref, { assignedClientIds: nextAssignedIds });
      }
    }

    const docsToDelete = new Map<string, Parameters<WriteBatch['delete']>[0]>();
    [
      ...commitmentSnapshot.docs,
      ...nestedCommitmentSnapshot.docs,
      ...loanLedgerDocs,
      ...ticketSnapshot.docs,
      ...nestedTicketSnapshot.docs,
      ...notificationSnapshot.docs,
      ...nestedNotificationSnapshot.docs,
      ...statementSnapshot.docs,
      ...relationshipAsPrimarySnapshot.docs,
      ...relationshipAsAssociatedSnapshot.docs,
      ...approvalAsClientSnapshot.docs,
      ...approvalAsUserSnapshot.docs,
      ...approvalAsTrusteeSnapshot.docs,
      ...activityLogSnapshot.docs,
      ...tokenSnapshot.docs,
      ...individualKycSnapshot.docs,
      ...corporateKycSnapshot.docs,
      ...kycDraftSnapshot.docs,
      ...corporateKycDraftSnapshot.docs,
      ...leadSnapshot.docs,
    ].forEach(docSnap => {
      docsToDelete.set(docSnap.ref.path, docSnap.ref);
    });

    docsToDelete.set(userRef.path, userRef);
    await this.deleteDocumentRefsInBatches([...docsToDelete.values()]);

    return {
      success: true,
      uid: userId,
      deleted,
    };
  }

  private async getLoanLedgerDocsForCommitments(commitmentIds: string[]) {
    if (commitmentIds.length === 0) {
      return [];
    }

    const docs = new Map<string, Awaited<ReturnType<typeof getDocs>>['docs'][number]>();

    for (let index = 0; index < commitmentIds.length; index += 10) {
      const idsChunk = commitmentIds.slice(index, index + 10);
      const snapshot = await getDocs(
        query(collection(FIRESTORE, 'loanLedger'), where('sourceId', 'in', idsChunk))
      );

      snapshot.docs.forEach(docSnap => {
        docs.set(docSnap.ref.path, docSnap);
      });
    }

    return [...docs.values()];
  }

  private async deleteDocumentRefsInBatches(docRefs: Parameters<WriteBatch['delete']>[0][]): Promise<void> {
    const batchSize = 450;

    for (let index = 0; index < docRefs.length; index += batchSize) {
      const batch = writeBatch(FIRESTORE);
      const refsChunk = docRefs.slice(index, index + batchSize);

      refsChunk.forEach(docRef => batch.delete(docRef));
      await batch.commit();
    }
  }


  // Client Leads Methods
  async addLead(leadData: Omit<ClientLead, 'id'>): Promise<void> {
    await addDoc(collection(FIRESTORE, 'clientLeads'), leadData);
  }

  async updateLead(leadId: string, leadData: Record<string, unknown>): Promise<void> {
    await updateDoc(doc(FIRESTORE, 'clientLeads', leadId), leadData);
  }

  async deleteLead(leadId: string): Promise<void> {
    await deleteDoc(doc(FIRESTORE, 'clientLeads', leadId));
  }

  async confirmFundsReceived(commitment: ClientCommitment, amount: number, userId: string, dateReceived?: string): Promise<void> {
    const now = new Date().toISOString();
    const effectiveDateReceived = dateReceived || now;
    const result = await this.executeFinancialAction<{ ledgerEntryId?: string; amount?: number }>('CONFIRM_FUNDS_RECEIVED', {
      commitmentId: commitment.id,
      userId: commitment.userId,
      amount,
      dateReceived: effectiveDateReceived,
      confirmedByUserId: userId,
      accountType: commitment.accountType,
    });

    this.dataService.updateLocalCommitment(commitment.id, {
      fundsReceived: true,
      isConfirmed: true,
      dateFundsReceived: effectiveDateReceived,
      dateConfirmed: commitment.dateConfirmed || now,
      confirmedByUserId: userId,
    });
    this.dataService.removeLocalLoanLedgerEntriesBySource(commitment.id, LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN);

    if (result?.ledgerEntryId) {
      this.dataService.upsertLocalLoanLedgerEntry({
        id: result.ledgerEntryId,
        loanDealId: commitment.loanDealId,
        date: now,
        valueDate: effectiveDateReceived,
        description: `Client funds received (${commitment.id})`,
        amount: result.amount ?? amount,
        entryType: LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN,
        sourceId: commitment.id,
        createdBy: userId,
        accountType: commitment.accountType,
        userId: commitment.userId,
        commitmentId: commitment.id,
      });
    }
  }

  async reverseFundsReceived(commitment: ClientCommitment): Promise<void> {
    await this.executeFinancialAction('REVERSE_FUNDS_RECEIVED', {
      commitmentId: commitment.id,
      userId: commitment.userId,
    });

    this.dataService.updateLocalCommitment(commitment.id, {
      fundsReceived: false,
      dateFundsReceived: null as any,
    });
    this.dataService.removeLocalLoanLedgerEntriesBySource(commitment.id, LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN);
  }

  

  

  

  

  

  

  

  

  

  /**
   * Get a user profile by ID
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      return await this.getCachedUserProfile(userId);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  // ── Delegation stubs for methods moved to ClientRelationshipService ────────

  async hasManagedClients(userId: string): Promise<boolean> {
    return this.relationshipService.hasManagedClients(userId);
  }

  async hasActiveRelationships(userId: string): Promise<boolean> {
    return this.relationshipService.hasActiveRelationships(userId);
  }

  async getManagedClients(userId: string): Promise<UserProfile[]> {
    return this.relationshipService.getManagedClients(userId);
  }

  async getAccessibleClients(userId: string): Promise<import('../models/relationship.model').AccessibleClient[]> {
    return this.relationshipService.getAccessibleClients(userId);
  }

  async getRelationshipsForClient(clientId: string): Promise<import('../models/relationship.model').ClientRelationship[]> {
    return this.relationshipService.getRelationshipsForClient(clientId);
  }

  async requestCreateClientRelationship(
    primaryClientId: string,
    primaryClientName: string,
    associatedUserId: string,
    associatedUserName: string,
    relationshipType: 'Trustee' | 'ReadOnly',
    canCommit: boolean = false
  ): Promise<string> {
    return this.relationshipService.requestCreateClientRelationship(
      primaryClientId, primaryClientName, associatedUserId, associatedUserName, relationshipType, canCommit
    );
  }

  async requestAddClientAccount(
    userId: string,
    userName: string,
    accountData: import('../models/account.model').ClientAccount
  ): Promise<string> {
    return this.relationshipService.requestAddClientAccount(userId, userName, accountData);
  }

  async createClientRelationship(
    primaryClientId: string,
    associatedUserId: string,
    relationshipType: 'Trustee' | 'ReadOnly',
    allowedAccounts?: string[],
    canCommitOverride?: boolean
  ): Promise<void> {
    return this.relationshipService.createClientRelationship(
      primaryClientId, associatedUserId, relationshipType, allowedAccounts, canCommitOverride
    );
  }

  async updateClientRelationship(
    primaryClientId: string,
    associatedUserId: string,
    relationshipType: 'Trustee' | 'ReadOnly',
    canCommitOverride?: boolean,
    allowedAccounts?: string[]
  ): Promise<void> {
    return this.relationshipService.updateClientRelationship(
      primaryClientId, associatedUserId, relationshipType, canCommitOverride, allowedAccounts
    );
  }

  async deactivateClientRelationship(relationshipId: string): Promise<void> {
    return this.relationshipService.deactivateClientRelationship(relationshipId);
  }

  async reactivateClientRelationship(relationshipId: string): Promise<void> {
    return this.relationshipService.reactivateClientRelationship(relationshipId);
  }

  async deleteClientRelationship(relationshipId: string): Promise<void> {
    return this.relationshipService.deleteClientRelationship(relationshipId);
  }

}
